import React, { useState, useEffect, useMemo } from 'react';
import {
    useReactTable,
    getCoreRowModel,
    getFilteredRowModel,
    getPaginationRowModel,
    flexRender,
    ColumnDef,
    PaginationState,
} from '@tanstack/react-table';
import { changeBlogStatus } from '../../services/dashboard/ChangeStatusBlog';
import { GetBlogs, Blog } from '../../services/dashboard/GetBlogs';
import LoadingSpinner from '../utility/LoadingSpinner';
import EditBlogModal from './EditBlogModal';
import CreateBlogModal from './CreateBlogModal';
import '../../styles/dash/Blogs.css';
import '../../styles/dash/Roles.css';
import '../../styles/dash/BotsSection.css';
import { useNavigate } from "react-router-dom";

// Interfaces para los permisos
interface Permission {
    Id: number;
    IdAction: number;
    Action: string;
    IdModule: number;
    ModuleName: string;
}

interface Role {
    Id: number;
    RoleName: string;
    Permissions: Permission[];
}

const Blogs: React.FC = () => {
    const [blogs, setBlogs] = useState<Blog[]>([]);
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState<string | null>(null);
    const [globalFilter, setGlobalFilter] = useState('');
    const [isUpdatingStatus, setIsUpdatingStatus] = useState(false);
    const [showEditModal, setShowEditModal] = useState(false);
    const [showCreateModal, setShowCreateModal] = useState(false);
    const [editingBlog, setEditingBlog] = useState<Blog | null>(null);
    const [pagination, setPagination] = useState<PaginationState>({
        pageIndex: 0,
        pageSize: 10,
    });
    const [permisos, setPermisos] = useState<number[]>([]);

    const navigate = useNavigate();

    // Obtener permisos del usuario desde localStorage
    useEffect(() => {
        let hasPermission = false;
        const userData = localStorage.getItem("user");
        if (userData) {
            try {
                const userObject = JSON.parse(userData);
                const userDataParsed = JSON.parse(userObject.userData || "{}");

                if (userDataParsed?.roles?.length) {
                    const permisosAplanados = userDataParsed.roles.flatMap((rol: Role) =>
                        rol.Permissions.map((p: Permission) => p.IdAction)
                    );
                    setPermisos(permisosAplanados);
                    if (permisosAplanados.includes(28)) {
                        hasPermission = true;
                    }
                }
            } catch (err) {
                console.error('Error parsing user data:', err);
            }
        }
        if (!hasPermission) {
            navigate("/login");
        }
    }, [navigate]);

    const fetchBlogs = async () => {
        try {
            setIsLoading(true);
            const blogsData = await GetBlogs();
            setBlogs(blogsData);
        } catch (err: any) {
            setError(err.message || 'Error al cargar los blogs');
            console.error('Error fetching blogs:', err);
        } finally {
            setIsLoading(false);
        }
    };

    useEffect(() => {
        fetchBlogs();
    }, []);

    const handleToggleStatus = async (id: number, currentStatus: number) => {
        // Verificar permiso para cambiar estado (ID 30 según tu especificación)
        if (!permisos.includes(30)) {
            setError('No tienes permisos para cambiar el estado de los blogs');
            return;
        }

        const originalBlogs = [...blogs];
        const newStatus = currentStatus === 1 ? 0 : 1;

        // Optimistic update
        setBlogs(prev =>
            prev.map(blog =>
                blog.id === id ? { ...blog, status: newStatus } : blog
            )
        );
        setIsUpdatingStatus(true);
        setError(null);

        try {
            await changeBlogStatus({ Id: id, Status: newStatus });
        } catch (err) {
            setBlogs(originalBlogs); // Revert on error
            setError(err instanceof Error ? err.message : 'No se pudo cambiar el estado del blog.');
        } finally {
            setIsUpdatingStatus(false);
        }
    };

    const handleOpenEditModal = (blog: Blog) => {
        // Verificar permiso para editar (ID 31 según tu especificación)
        if (!permisos.includes(31)) {
            setError('No tienes permisos para editar blogs');
            return;
        }

        setEditingBlog(blog);
        setShowEditModal(true);
    };

    const handleCloseEditModal = () => {
        setShowEditModal(false);
        setEditingBlog(null);
    };

    const handleEditSuccess = (updatedBlog: Blog) => {
        fetchBlogs();
        handleCloseEditModal();
    };

    const handleCreateSuccess = () => {
        setShowCreateModal(false);
        fetchBlogs();
        handleCloseEditModal();
    };

    function getPaginationRange(current: number, total: number): (number | string)[] {
        const delta = 1;
        const range: (number | string)[] = [];
        const rangeWithDots: (number | string)[] = [];
        let l = 0;

        for (let i = 1; i <= total; i++) {
            if (i === 1 || i === total || (i >= current - delta && i <= current + delta)) {
                range.push(i);
            }
        }

        for (let i of range) {
            if (l > 0) {
                if (Number(i) - l === 2) {
                    rangeWithDots.push(l + 1);
                } else if (Number(i) - l !== 1) {
                    rangeWithDots.push("...");
                }
            }
            rangeWithDots.push(i);
            l = Number(i);
        }

        return rangeWithDots;
    }

    const columns = useMemo<ColumnDef<Blog>[]>(() => [
        {
            id: 'actions',
            header: 'Opciones',
            cell: ({ row }) => {
                const blog = row.original;
                const powerButtonColorClass = blog.status === 1 ? 'power-off' : 'power-on';
                const powerButtonText = blog.status === 1 ? 'Desactivar' : 'Activar';

                return (
                    <div className="table-actions">
                        <button
                            className="action-button edit"
                            title="Editar"
                            disabled={isUpdatingStatus || !permisos.includes(31)}
                            onClick={() => handleOpenEditModal(blog)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" fill="white" viewBox="0 0 24 24">
                                <path d="M3 17.25V21h3.75L17.81 9.94l-3.75-3.75L3 17.25z" />
                                <path d="M20.71 7.04a1.003 1.003 0 0 0 0-1.41l-2.34-2.34a1.003 1.003 0 0 0-1.41 0L15.13 4.9l3.75 3.75 1.83-1.61z" />
                            </svg>
                        </button>
                        <button
                            className={`action-button power ${powerButtonColorClass}`}
                            title={powerButtonText}
                            onClick={() => handleToggleStatus(blog.id, blog.status)}
                            disabled={isUpdatingStatus || !permisos.includes(30)}
                        >
                            <svg xmlns="http://www.w3.org/2000/svg" width="16" height="16" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3" strokeLinecap="round" strokeLinejoin="round">
                                <circle cx="12" cy="12" r="9" />
                                <line x1="12" y1="4" x2="12" y2="8" />
                            </svg>
                        </button>
                    </div>
                );
            },
        },
        {
            accessorKey: 'title',
            header: 'Título',
            cell: info => <span >{info.getValue<string>()}</span>,
        },
        {
            accessorKey: 'date',
            header: 'Fecha de Publicación',
            cell: info => info.getValue<string>(),
        },
        {
            accessorKey: 'imageName',
            header: 'Imagen',
            cell: info => <span className="blog-seo-column">{info.getValue<string>()}</span>,
        },
        {
            accessorKey: 'seo',
            header: 'SEO',
            cell: info => <span className="blog-seo-column">{info.getValue<string>()}</span>,
        },
        {
            accessorKey: 'language',
            header: 'Idioma',
            cell: info => <span >{info.getValue<string>()}</span>,
        },
        {
            accessorKey: 'status',
            header: 'Estado',
            cell: ({ getValue }) => {
                const status = getValue<number>();
                const statusText = status === 1 ? 'Activo' : 'Inactivo';
                const statusClass = status === 1 ? 'activo' : 'inactivo';
                return (
                    <span className={`status-text ${statusClass}`}>
                        {statusText}
                    </span>
                );
            },
        },
    ], [isUpdatingStatus, permisos]);

    const table = useReactTable({
        data: blogs,
        columns,
        state: {
            globalFilter,
            pagination,
        },
        onGlobalFilterChange: setGlobalFilter,
        onPaginationChange: setPagination,
        getCoreRowModel: getCoreRowModel(),
        getFilteredRowModel: getFilteredRowModel(),
        getPaginationRowModel: getPaginationRowModel(),
    });

    if (isLoading) {
        return <LoadingSpinner />;
    }

    if (error) {
        return <div className="alert alert-danger">{error}</div>;
    }

    return (
        <div className="blogs-container">
            {isUpdatingStatus && (
                <div className="overlay-spinner">
                    <LoadingSpinner />
                </div>
            )}

            <div className="table-header-toolbar">
                <button
                    className="add-bot-tab-button"
                    onClick={() => setShowCreateModal(true)}
                    disabled={!permisos.includes(29)} // ID 29 para crear blogs
                >
                    Crear Nuevo Blog
                </button>
                <div className="search-container">
                    <input
                        type="text"
                        placeholder="Buscar en blogs..."
                        value={globalFilter}
                        onChange={(e) => setGlobalFilter(e.target.value)}
                        className="global-filter"
                    />
                </div>
            </div>
            <div className="roles-table-container">
                <table className="demo-request-table">
                    <thead>
                        {table.getHeaderGroups().map(headerGroup => (
                            <tr key={headerGroup.id}>{headerGroup.headers.map(header => (<th key={header.id}>{flexRender(header.column.columnDef.header, header.getContext())}</th>))}</tr>
                        ))}
                    </thead>
                    <tbody>
                        {table.getRowModel().rows.map(row => (
                            <tr key={row.id}>{row.getVisibleCells().map(cell => (<td key={cell.id}>{flexRender(cell.column.columnDef.cell, cell.getContext())}</td>))}</tr>
                        ))}
                    </tbody>
                </table>

                <div className="pagination-controls">
                    <button
                        onClick={() => table.previousPage()}
                        disabled={!table.getCanPreviousPage()}
                    >
                        ←
                    </button>

                    {getPaginationRange(
                        table.getState().pagination.pageIndex + 1,
                        table.getPageCount()
                    ).map((page, idx) =>
                        page === "..." ? (
                            <span key={idx} className="dots">...</span>
                        ) : (
                            <button
                                key={idx}
                                className={
                                    table.getState().pagination.pageIndex + 1 === page
                                        ? "active-page"
                                        : ""
                                }
                                onClick={() => table.setPageIndex(Number(page) - 1)}
                            >
                                {page}
                            </button>
                        )
                    )}

                    <button
                        onClick={() => table.nextPage()}
                        disabled={!table.getCanNextPage()}
                    >
                        →
                    </button>
                </div>
            </div>

            <CreateBlogModal
                show={showCreateModal}
                onClose={() => setShowCreateModal(false)}
                onSuccess={handleCreateSuccess}
            />

            <EditBlogModal
                blog={editingBlog}
                show={showEditModal}
                onClose={handleCloseEditModal}
                onSuccess={handleEditSuccess}
            />
        </div>
    );
};

export default Blogs;